import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import {
  keycloakApi,
  KeycloakConfig,
  KeycloakAuthRequest,
  KeycloakAuthFlow,
} from '../services/integrations-api';

export const KeycloakManager: React.FC = () => {
  const [instances, setInstances] = useState<KeycloakConfig[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<KeycloakConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [waitingForAuthCode, setWaitingForAuthCode] = useState(false);
  const [editingInstance, setEditingInstance] = useState<KeycloakConfig | null>(null);

  const [authForm, setAuthForm] = useState<KeycloakAuthRequest>({
    instanceName: '',
    url: '',
    realm: '',
    clientId: '',
    clientSecret: '',
    authFlow: 'client_credentials',
    redirectUri: '',
    scopes: ['openid'],
  });

  const [scopesInput, setScopesInput] = useState<string>('openid');

  useEffect(() => {
    loadInstances();

    // Check if we were redirected back from OAuth with an authorization code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      // Clear the code from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Restore the form state from sessionStorage
      const savedFormState = sessionStorage.getItem('keycloak_oauth_form');
      if (savedFormState) {
        const completeAuth = async () => {
          try {
            const formData = JSON.parse(savedFormState);

            // Ensure redirectUri is set properly
            const redirectUri = formData.redirectUri || (window.location.origin + '?keycloak_callback=1');
            const restoredForm = {
              ...formData,
              authorizationCode: code,
              redirectUri: redirectUri
            };

            setAuthForm(restoredForm);
            setScopesInput(formData._scopesInput || 'openid');
            sessionStorage.removeItem('keycloak_oauth_form');

            // Automatically complete the authentication
            const scopes = (formData._scopesInput || 'openid').split(',').map((s: string) => s.trim()).filter(Boolean);
            setLoading(true);

            console.log('Attempting to authenticate with Keycloak:', {
              url: restoredForm.url,
              realm: restoredForm.realm,
              clientId: restoredForm.clientId,
              redirectUri: restoredForm.redirectUri,
              hasCode: !!code,
            });

            try {
              await keycloakApi.authenticate({ ...restoredForm, scopes });
              await loadInstances();
              setSuccess('Successfully authenticated with Keycloak!');
              console.log('Keycloak authentication successful');
            } catch (err: any) {
              console.error('Keycloak authentication failed:', err);
              setError(err.response?.data?.error || err.message || 'Authentication failed');
              // On error, show the dialog so user can see what went wrong
              setAuthDialogOpen(true);
              setWaitingForAuthCode(true);
            } finally {
              setLoading(false);
            }
          } catch (err) {
            console.error('Failed to restore form state:', err);
            setError('Failed to complete authentication. Please try again.');
            setLoading(false);
          }
        };

        completeAuth();
      }
    }
  }, []);

  const loadInstances = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await keycloakApi.getAllInstances();
      setInstances(data);

      // Update selected instance to get fresh data
      if (selectedInstance) {
        const updatedInstance = data.find(i => i.id === selectedInstance.id);
        if (updatedInstance) {
          setSelectedInstance(updatedInstance);
        }
      } else if (data.length > 0) {
        // Select first instance if none selected
        setSelectedInstance(data[0]);
      }
    } catch (err: any) {
      console.error('Failed to load Keycloak instances:', err);
      // Don't set error for auth issues - Keycloak integrations are optional
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.message || 'Failed to load Keycloak instances');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditInstance = (instance: KeycloakConfig) => {
    setEditingInstance(instance);
    setAuthForm({
      instanceId: instance.id,
      instanceName: instance.name,
      url: instance.url,
      realm: instance.realm,
      clientId: instance.clientId,
      clientSecret: '', // Don't pre-fill secret
      authFlow: instance.authFlow,
      redirectUri: instance.redirectUri || '',
      scopes: instance.scopes || ['openid'],
    });
    setScopesInput((instance.scopes || ['openid']).join(', '));
    setAuthDialogOpen(true);
  };

  const handleSaveInstance = async () => {
    if (!editingInstance) return;

    try {
      setLoading(true);
      setError(null);

      // Convert scopesInput to array
      const scopes = scopesInput.split(',').map(s => s.trim()).filter(Boolean);

      // Update the instance (without re-authenticating)
      await keycloakApi.updateInstance(editingInstance.id, {
        name: authForm.instanceName,
        url: authForm.url,
        realm: authForm.realm,
        clientId: authForm.clientId,
        authFlow: authForm.authFlow,
        redirectUri: authForm.redirectUri,
        scopes: scopes,
        // Only update client secret if provided
        ...(authForm.clientSecret && { clientSecret: authForm.clientSecret }),
      });

      // Reload instances
      await loadInstances();

      setSuccess('Keycloak instance updated successfully!');
      setAuthDialogOpen(false);
      setEditingInstance(null);
      resetAuthForm();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert scopesInput to array for the API call
      const scopes = scopesInput.split(',').map(s => s.trim()).filter(Boolean);

      // For authorization code flow, we need to redirect to Keycloak
      if (authForm.authFlow === 'authorization_code' && !authForm.authorizationCode) {
        // Save form state to sessionStorage so we can restore it after OAuth redirect
        sessionStorage.setItem('keycloak_oauth_form', JSON.stringify({
          ...authForm,
          _scopesInput: scopesInput,
        }));

        // Also save a flag to reopen the settings dialog
        sessionStorage.setItem('keycloak_oauth_redirect', 'true');

        // Generate authorization URL and open it
        const redirectUri = authForm.redirectUri || window.location.origin + '?keycloak_callback=1';
        const authUrl = await keycloakApi.getAuthUrl({
          url: authForm.url,
          realm: authForm.realm,
          clientId: authForm.clientId,
          redirectUri: redirectUri,
          scopes: scopes,
          state: Math.random().toString(36).substring(7),
        });

        // Open in new window or redirect
        window.open(authUrl, '_blank');
        setWaitingForAuthCode(true);
        setLoading(false);
        return;
      }

      await keycloakApi.authenticate({ ...authForm, scopes });

      // Reload instances
      await loadInstances();

      setSuccess('Successfully authenticated with Keycloak!');
      setAuthDialogOpen(false);
      setWaitingForAuthCode(false);
      resetAuthForm();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInstance = async (instanceId: string) => {
    if (!confirm('Are you sure you want to delete this Keycloak instance?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await keycloakApi.deleteInstance(instanceId);

      // If deleted instance was selected, clear selection
      if (selectedInstance?.id === instanceId) {
        setSelectedInstance(null);
      }

      // Reload instances
      await loadInstances();
      setSuccess('Keycloak instance deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete instance');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async (instanceId: string) => {
    try {
      setLoading(true);
      setError(null);

      await keycloakApi.refreshToken(instanceId);
      await loadInstances();

      setSuccess('Token refreshed successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to refresh token');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setError(null);

      const result = await keycloakApi.testConnection(
        authForm.url || selectedInstance?.url || '',
        authForm.realm || selectedInstance?.realm || ''
      );

      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const resetAuthForm = () => {
    setAuthForm({
      instanceName: '',
      url: '',
      realm: '',
      clientId: '',
      clientSecret: '',
      authFlow: 'client_credentials',
      redirectUri: '',
      scopes: ['openid'],
    });
    setScopesInput('openid');
    setWaitingForAuthCode(false);
    setEditingInstance(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isTokenExpired = (expiration?: string) => {
    if (!expiration) return false;
    return new Date(expiration) < new Date();
  };

  const getTokenStatus = (instance: KeycloakConfig) => {
    if (!instance.accessToken) return { color: 'default' as const, label: 'No Token' };
    if (isTokenExpired(instance.tokenExpiration)) return { color: 'error' as const, label: 'Expired' };

    const expiresAt = new Date(instance.tokenExpiration!);
    const now = new Date();
    const minutesRemaining = Math.floor((expiresAt.getTime() - now.getTime()) / 1000 / 60);

    if (minutesRemaining < 5) return { color: 'warning' as const, label: `Expires in ${minutesRemaining}m` };
    return { color: 'success' as const, label: 'Active' };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Keycloak OAuth Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<SecurityIcon />}
          onClick={() => setAuthDialogOpen(true)}
          disabled={loading}
        >
          Add Keycloak Instance
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && instances.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : instances.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Keycloak Instances Configured
            </Typography>
            <Typography color="text.secondary" paragraph>
              Configure a Keycloak instance to enable OAuth authentication for RangeOS environments.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SecurityIcon />}
              onClick={() => setAuthDialogOpen(true)}
            >
              Add Your First Keycloak Instance
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {instances.map((instance) => (
            <Accordion key={instance.id} defaultExpanded={instances.length === 1}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <SecurityIcon sx={{ mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{instance.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {instance.url} / {instance.realm}
                    </Typography>
                  </Box>
                  <Chip
                    label={getTokenStatus(instance).label}
                    color={getTokenStatus(instance).color}
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Chip
                    label={instance.authFlow === 'client_credentials' ? 'Client Credentials' : 'Auth Code'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Instance Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Keycloak URL
                            </Typography>
                            <Typography variant="body1">{instance.url}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Realm
                            </Typography>
                            <Typography variant="body1">{instance.realm}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Client ID
                            </Typography>
                            <Typography variant="body1">{instance.clientId}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Auth Flow
                            </Typography>
                            <Typography variant="body1">
                              {instance.authFlow === 'client_credentials'
                                ? 'Client Credentials'
                                : 'Authorization Code'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Last Authenticated
                            </Typography>
                            <Typography variant="body1">{formatDate(instance.lastAuthenticated)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Token Expiration
                            </Typography>
                            <Typography variant="body1">{formatDate(instance.tokenExpiration)}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditInstance(instance)}
                        disabled={loading}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => handleRefreshToken(instance.id)}
                        disabled={loading || (!instance.refreshToken && instance.authFlow !== 'client_credentials')}
                      >
                        Refresh Token
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteInstance(instance.id)}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Authentication Dialog */}
      <Dialog open={authDialogOpen} onClose={() => { setAuthDialogOpen(false); resetAuthForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingInstance ? 'Edit Keycloak Instance' : 'Add Keycloak Instance'}</DialogTitle>
        <DialogContent>
          {editingInstance && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Editing configuration settings. This will not re-authenticate or refresh tokens.
                Use the "Refresh Token" button to get new tokens if needed.
              </Typography>
            </Alert>
          )}
          {waitingForAuthCode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Authorization window opened!</strong>
              </Typography>
              <Typography variant="body2" gutterBottom>
                1. Complete the login in the opened window
              </Typography>
              <Typography variant="body2" gutterBottom>
                2. After logging in, you'll be redirected back here
              </Typography>
              <Typography variant="body2">
                3. The authorization code will be automatically copied to your clipboard
              </Typography>
              <Typography variant="body2">
                4. Paste it in the "Authorization Code" field below and click "Complete Authentication"
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Instance Name"
              value={authForm.instanceName}
              onChange={(e) => setAuthForm({ ...authForm, instanceName: e.target.value })}
              fullWidth
              helperText="Friendly name for this Keycloak instance"
              disabled={waitingForAuthCode || !!editingInstance}
            />

            <TextField
              label="Keycloak URL"
              value={authForm.url}
              onChange={(e) => setAuthForm({ ...authForm, url: e.target.value })}
              fullWidth
              placeholder="https://keycloak.example.com"
              helperText="Base URL of your Keycloak server"
              disabled={waitingForAuthCode}
            />

            <TextField
              label="Realm"
              value={authForm.realm}
              onChange={(e) => setAuthForm({ ...authForm, realm: e.target.value })}
              fullWidth
              helperText="Keycloak realm name"
              disabled={waitingForAuthCode}
            />

            <TextField
              label="Client ID"
              value={authForm.clientId}
              onChange={(e) => setAuthForm({ ...authForm, clientId: e.target.value })}
              fullWidth
              disabled={waitingForAuthCode}
            />

            <FormControl component="fieldset" disabled={waitingForAuthCode}>
              <FormLabel component="legend">Authentication Flow</FormLabel>
              <RadioGroup
                value={authForm.authFlow}
                onChange={(e) =>
                  setAuthForm({ ...authForm, authFlow: e.target.value as KeycloakAuthFlow })
                }
              >
                <FormControlLabel
                  value="client_credentials"
                  control={<Radio />}
                  label="Client Credentials (for server-to-server)"
                />
                <FormControlLabel
                  value="authorization_code"
                  control={<Radio />}
                  label="Authorization Code (for user authentication)"
                />
              </RadioGroup>
            </FormControl>

            {authForm.authFlow === 'client_credentials' && (
              <TextField
                label="Client Secret"
                type="password"
                value={authForm.clientSecret}
                onChange={(e) => setAuthForm({ ...authForm, clientSecret: e.target.value })}
                fullWidth
                required={!editingInstance}
                disabled={waitingForAuthCode}
                helperText={editingInstance ? 'Leave empty to keep existing secret' : undefined}
              />
            )}

            {authForm.authFlow === 'authorization_code' && (
              <>
                <TextField
                  label="Client Secret"
                  type="password"
                  value={authForm.clientSecret}
                  onChange={(e) => setAuthForm({ ...authForm, clientSecret: e.target.value })}
                  fullWidth
                  helperText={editingInstance ? 'Leave empty to keep existing secret (optional for public clients)' : 'Optional for public clients'}
                  disabled={waitingForAuthCode}
                />
                <TextField
                  label="Redirect URI"
                  value={authForm.redirectUri}
                  onChange={(e) => setAuthForm({ ...authForm, redirectUri: e.target.value })}
                  fullWidth
                  placeholder={window.location.origin}
                  helperText={`Leave empty to use default: ${window.location.origin}`}
                  disabled={waitingForAuthCode}
                />
                <TextField
                  label="Authorization Code"
                  value={authForm.authorizationCode || ''}
                  onChange={(e) => setAuthForm({ ...authForm, authorizationCode: e.target.value })}
                  fullWidth
                  helperText="Paste the code from OAuth callback here"
                  autoFocus={waitingForAuthCode}
                />
              </>
            )}

            <TextField
              label="Scopes"
              value={scopesInput}
              onChange={(e) => setScopesInput(e.target.value)}
              fullWidth
              placeholder="openid, profile, email"
              helperText="Comma-separated list of OAuth scopes"
              disabled={waitingForAuthCode}
            />

            {authForm.url && authForm.realm && (
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testingConnection}
                startIcon={testingConnection ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              >
                Test Connection
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAuthDialogOpen(false); resetAuthForm(); }} disabled={loading}>
            Cancel
          </Button>
          {editingInstance ? (
            <Button
              onClick={handleSaveInstance}
              variant="contained"
              disabled={
                loading ||
                !authForm.url ||
                !authForm.realm ||
                !authForm.clientId
              }
              startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
            >
              Save Changes
            </Button>
          ) : (
            <Button
              onClick={handleAuthenticate}
              variant="contained"
              disabled={
                loading ||
                !authForm.url ||
                !authForm.realm ||
                !authForm.clientId ||
                (authForm.authFlow === 'client_credentials' && !authForm.clientSecret) ||
                (waitingForAuthCode && !authForm.authorizationCode)
              }
              startIcon={loading ? <CircularProgress size={16} /> : <VpnKeyIcon />}
            >
              {waitingForAuthCode ? 'Complete Authentication' :
               authForm.authFlow === 'authorization_code' ? 'Open Authorization Page' :
               'Authenticate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
